import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  styled
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Image as ImageIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { fetchProfile as apiFetchProfile, updateProfile } from '../utils/api';
import ImageCropper from '../components/ImageCropper';

const INK = {
  primaria: 'rgba(255, 255, 255, 0.92)',
  secundaria: 'rgba(255, 255, 255, 0.56)',
  borde: 'rgba(255, 255, 255, 0.10)',
  bordeFuerte: 'rgba(255, 255, 255, 0.18)',
  hover: 'rgba(255, 255, 255, 0.09)',
  media: 'rgba(255, 255, 255, 0.06)'
};

// La vista previa y su boton son un solo grupo. Antes el contenedor ocupaba
// todo el ancho del formulario, asi que el `right: 0` del boton lo mandaba al
// borde derecho de la pagina: el texto decia "tocá el icono de la camara" y el
// icono estaba a trescientos setenta pixeles de la imagen que cambia.
const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '176px',
  marginBottom: theme.spacing(4),
  // En pantalla angosta la previa se achica y comparte fila con su
  // explicacion: sola en medio del ancho se veia perdida.
  [theme.breakpoints.down('sm')]: {
    width: '112px',
    marginBottom: 0
  }
}));

// La portada se ve en 4:5 en la grilla de colecciones. Previsualizarla en un
// circulo mostraba un recorte que no existe en ninguna pantalla del producto.
const ProfileAvatar = styled(Box)(({ theme }) => ({
  width: '176px',
  aspectRatio: '4 / 5',
  borderRadius: 8,
  overflow: 'hidden',
  backgroundColor: INK.media,
  border: `1px solid ${INK.borde}`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  [theme.breakpoints.down('sm')]: { width: '112px' }
}));

const UploadButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: 8,
  right: 8,
  width: 36,
  height: 36,
  color: INK.primaria,
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.72)' },
  // Sobre una previa de 112px, 36 se comian un tercio del ancho. 32 sigue por
  // encima del minimo de area tactil y no domina la imagen.
  [theme.breakpoints.down('sm')]: {
    width: 32,
    height: 32,
    bottom: 6,
    right: 6
  }
}));

// El acento del tema solo pinta el borde con el foco puesto; en reposo los
// campos son un rectangulo neutral como cualquier otro limite de la interfaz.
const campoSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '0.9375rem',
    '& fieldset': {
      borderColor: INK.borde,
      transition: 'border-color 160ms ease'
    },
    '&:hover fieldset': { borderColor: INK.bordeFuerte },
    // Color y grosor explicitos: dejar solo el grosor heredaba el borderColor
    // de la regla de arriba y el campo enfocado quedaba identico al de reposo.
    '&.Mui-focused fieldset': {
      borderColor: '#bb86fc',
      borderWidth: '2px'
    }
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.9375rem',
    color: INK.secundaria,
    '&.Mui-focused': { color: '#bb86fc' }
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& .MuiOutlinedInput-root fieldset': { transition: 'none' }
  }
};

const ProfileForm = () => {
  const { profileId } = useParams();
  const isEditMode = Boolean(profileId);
  const [profile, setProfile] = useState({
    name: '',
    description: '',
    cover_image: null,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [originalImage, setOriginalImage] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Definir fetchProfile con useCallback para evitar el problema de referencia
  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await apiFetchProfile(profileId);

      if (error) throw error;

      if (data) {
        setProfile(data);
        if (data.cover_image) {
          setImagePreview(data.cover_image);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (isEditMode) {
      fetchProfile();
    }
  }, [profileId, user, isEditMode, navigate, fetchProfile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const imageUrl = URL.createObjectURL(file);
      setOriginalImage(imageUrl);
      setCropperOpen(true);
    }
  };

  const handleCropComplete = (croppedBlob, croppedImageUrl) => {
    setImageFile(croppedBlob);
    setImagePreview(croppedImageUrl);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!profile.name.trim()) {
      setError('Profile name is required');
      return;
    }

    try {
      setSaving(true);
      
      // Crear o actualizar perfil usando la nueva API
      await updateProfile(
        isEditMode ? profileId : null, 
        {
          name: profile.name,
          description: profile.description || '',
          user_id: user?.id // Pasar el ID del usuario si está disponible
        },
        imageFile
      );
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 1500);
      
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to save profile. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ pt: 10, pb: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    // Sin Paper con borde: el formulario es lo unico que hay en la pantalla y
    // no necesita una caja que lo separe de nada.
    <Container maxWidth="sm" sx={{ pt: { xs: 8, sm: 10 }, pb: 8 }}>
      <Box sx={{ mb: 1 }}>
        <Button
          onClick={() => navigate('/gallery')}
          startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
          sx={{
            ml: -1,
            px: 1,
            fontSize: '0.8125rem',
            fontWeight: 400,
            color: INK.secundaria,
            '&:hover': { color: INK.primaria, backgroundColor: 'transparent' }
          }}
        >
          Collections
        </Button>
      </Box>

      <Typography
        component="h1"
        sx={{
          fontSize: { xs: '1.5rem', sm: '1.75rem' },
          fontWeight: 500,
          letterSpacing: '-0.01em',
          color: INK.primaria,
          mb: 5
        }}
      >
        {isEditMode ? 'Edit collection' : 'New collection'}
      </Typography>

        <form onSubmit={handleSubmit}>
          <Typography
            component="label"
            sx={{ display: 'block', mb: 1, fontSize: '0.8125rem', color: INK.secundaria }}
          >
            Cover image — optional
          </Typography>

          {/* En movil, previa y explicacion van en fila; en escritorio la
              previa va sola y la explicacion sobra porque hay sitio de resto. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: { xs: 4, sm: 0 }
            }}
          >
          <ProfileImageContainer>
            <ProfileAvatar
              role="img"
              aria-label={imagePreview ? 'Cover image preview' : 'No cover image yet'}
              sx={
                imagePreview
                  ? { backgroundImage: `url(${imagePreview})` }
                  : {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(255, 255, 255, 0.28)'
                    }
              }
            >
              {/* El marcador anterior era un SVG que decia "Profile · Click to
                  view gallery": texto de otra pantalla dentro del formulario.
                  Que es opcional lo dice la etiqueta, no el hueco. */}
              {!imagePreview && <ImageIcon sx={{ fontSize: 28 }} />}
            </ProfileAvatar>

            <UploadButton
              aria-label={imagePreview ? 'Change cover image' : 'Add cover image'}
              component="label"
            >
              <input hidden accept="image/*" type="file" onChange={handleImageChange} />
              <PhotoCameraIcon sx={{ fontSize: 18 }} />
            </UploadButton>
          </ProfileImageContainer>

            <Typography
              sx={{
                display: { xs: 'block', sm: 'none' },
                fontSize: '0.8125rem',
                lineHeight: 1.45,
                color: INK.secundaria
              }}
            >
              {imagePreview
                ? 'Tap the camera to use a different photo.'
                : 'Tap the camera to pick a photo. It is what identifies the collection in the grid.'}
            </Typography>
          </Box>

          {/* Un solo borde por control: el campo lo lleva, el formulario no. */}
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={profile.name}
            onChange={handleChange}
            required
            variant="outlined"
            sx={campoSx}
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={profile.description || ''}
            onChange={handleChange}
            multiline
            rows={3}
            variant="outlined"
            sx={{ ...campoSx, mt: 2.5 }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}

          {/* Guardar es la unica accion de la pantalla y lleva el acento del
              producto; cancelar la acompana sin competir. */}
          {/* En tactil las acciones ocupan el ancho y se apilan, que es donde
              el pulgar las busca; en escritorio van en fila a la izquierda. */}
          <Box
            sx={{
              mt: 5,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1
            }}
          >
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disableElevation
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                borderRadius: '8px',
                px: 2.5,
                py: { xs: 1.25, sm: 1 },
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              {saving ? 'Saving' : isEditMode ? 'Save changes' : 'Create collection'}
            </Button>
            <Button
              onClick={() => navigate('/gallery')}
              sx={{
                borderRadius: '8px',
                px: 2,
                py: { xs: 1.25, sm: 1 },
                fontSize: '0.875rem',
                fontWeight: 500,
                color: INK.secundaria,
                '&:hover': { color: INK.primaria, backgroundColor: INK.hover }
              }}
            >
              Cancel
            </Button>
          </Box>
        </form>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
      >
        <Alert severity="success">
          {isEditMode ? 'Collection updated successfully!' : 'Collection created successfully!'}
        </Alert>
      </Snackbar>
      
      <ImageCropper
        open={cropperOpen}
        onClose={() => setCropperOpen(false)}
        image={originalImage}
        onCropComplete={handleCropComplete}
      />
    </Container>
  );
};

export default ProfileForm;
